'use client';

import { useRef } from 'react';
import { setInteractionModality } from 'react-aria';
import type { PressEvent, PressEvents } from 'react-aria';
import type { MenuItemProps as AriaMenuItemProps } from 'react-aria-components';

type MenuItemActionProps<T extends object> = Pick<AriaMenuItemProps<T>, 'onAction'> &
    Pick<PressEvents, 'onPress' | 'onPressStart'>;

/**
 * React Aria calls element.click() if no native click arrives within 80 ms of pointer release,
 * compensating for mobile browsers that omit clicks after long presses. Safari can still deliver
 * its native click later, duplicating raw menu actions; the synthetic click also enables focus rings.
 * Use filtered presses to dedupe actions and repair focus visibility, but retain keyboard actions
 * on keydown before the owning menu unmounts.
 *
 * Fallback implementation and rationale:
 * https://github.com/adobe/react-spectrum/blob/4693fcc844a341107e7dd26fe456edb1e269e44c/packages/react-aria/src/interactions/usePress.ts#L663-L686
 * Upstream discussion of the timeout and unwanted focus rings:
 * https://github.com/adobe/react-spectrum/pull/7720
 */
export const useMenuItemAction = <T extends object>({
    onAction,
    onPress,
    onPressStart,
}: MenuItemActionProps<T>): MenuItemActionProps<T> => {
    // Aria starts the keyboard press before its shortcut click, within the same keydown.
    // A ref keeps that input type available before a React state update would be visible.
    const pointerTypeRef = useRef<PressEvent['pointerType'] | null>(null);

    /**
     * Captures input type before nested menu activation can occur.
     */
    const handlePressStart = (event: PressEvent) => {
        pointerTypeRef.current = event.pointerType;
        onPressStart?.(event);
    };

    /**
     * Uses the filtered press path for physical and virtual pointer input.
     */
    const handlePress = (event: PressEvent) => {
        // Aria classifies its delayed fallback .click() as virtual input. Repair that before
        // consumer callbacks can render a focus ring, preserving real keyboard/virtual presses.
        if (event.pointerType === 'touch' || event.pointerType === 'mouse' || event.pointerType === 'pen') {
            setInteractionModality('pointer');
        }
        onPress?.(event);
        if (event.pointerType === 'keyboard') return;

        pointerTypeRef.current = null;
        onAction?.();
    };

    /**
     * Keeps keyboard actions ahead of the menu's keydown dismissal.
     */
    const handleAction = () => {
        if (pointerTypeRef.current !== 'keyboard') return;

        pointerTypeRef.current = null;
        onAction?.();
    };

    return {
        onAction: handleAction,
        onPress: handlePress,
        onPressStart: handlePressStart,
    };
};
